import React from 'react';

import RequireAuth from '@app/router/RequireAuth';
import { RouterScrollToTop } from '@app/router/RouterScrollToTop';
import LoginForm from '@FW/components/Forms/Auth/LoginForm';
import PasswordRecoveryForm from '@FW/components/Forms/Auth/PasswordRecoveryForm';
import { SelectClientForm } from '@FW/components/Forms/Auth/SelectClientForm';
import VerificationForm from '@FW/components/Forms/Auth/VerificationForm';
import { withLoading } from '@hocs/shared/withLoading.hoc';
import Error500 from '@pages/shared/Error/Error500';
import Home from '@pages/shared/Home/Home';
import ChangePassword from '@shared/pages/Auth/ChangePassword';
import MaintenanceMode from '@shared/pages/Error/MaintenanceMode';
import WIP from '@shared/pages/Error/WIP';
import { Button, Flex } from 'antd';
import { t } from 'i18next';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';

const MainLayout = React.lazy(() => import('@shared/layout/MainLayout/MainLayout'));
const AuthLayout = React.lazy(() => import('@shared/layout/AuthLayout/AuthLayout'));
const Logout = React.lazy(() => import('./Logout'));
const Error404Page = React.lazy(() => import('@pages/shared/Error/Error404'));

const WalletsOverviewPage = React.lazy(() => import('@pages/FW/WalletsOverview/WalletsOverview'));
const WalletPage = React.lazy(() => import('@pages/FW/Wallet/Wallet'));
const TransitWalletPage = React.lazy(() => import('@pages/FW/Wallet/TransitWallet'));
const BlockedResourcesWalletPage = React.lazy(
	() => import('@pages/FW/Wallet/BlockedResourcesWallet'),
);
const CardsOverviewPage = React.lazy(() => import('@pages/FW/Cards/Cards'));
const CardPage = React.lazy(() => import('@pages/FW/Card/Card'));
const AZKOverviewPage = React.lazy(() => import('@pages/FW/TerminalNetwork/TerminalNetwork'));
const AZKPage = React.lazy(() => import('@pages/FW/AZK/AZK'));
const ReportsPage = React.lazy(() => import('@pages/FW/ReportsOverview/ReportsOverview'));
const AccountPage = React.lazy(() => import('@pages/FW/Account/Account'));
const MoneyDepositPage = React.lazy(() => import('@pages/FW/MoneyDeposit/MoneyDeposit'));
const TransactionsOverviewPage = React.lazy(() => import('@FW/pages/Transactions/Transactions'));
const FaqPage = React.lazy(() => import('@pages/shared/FAQ/FAQ'));
const GenerateReportPage = React.lazy(() => import('@FW/Report/Report'));
const NotificationsPage = React.lazy(() => import('@pages/FW/Notifications/Notifications'));
const EDOPage = React.lazy(() => import('@pages/FW/EDO/EDO'));
const FuelPricesPage = React.lazy(() => import('@pages/FW/FuelPrices/FuelPrices'));
const RegistrationPage = React.lazy(() => import('@shared/pages/Auth/RegistrationPage'));
const ApiDocsPage = React.lazy(() => import('@FW/features/apiDocs/pages/ApiDocsPage'));

const AuthLayoutFallback = withLoading(AuthLayout);
const LogoutFallback = withLoading(Logout);
const MainLayoutFallback = withLoading(MainLayout);

const WalletsFallback = withLoading(WalletsOverviewPage);
const WalletFallback = withLoading(WalletPage);
const TransitWalletFallback = withLoading(TransitWalletPage);
const BlockedResourcesWalletFallback = withLoading(BlockedResourcesWalletPage);
const CardsFallback = withLoading(CardsOverviewPage);
const CardFallback = withLoading(CardPage);
const AZKOverviewFallback = withLoading(AZKOverviewPage);
const AZKFallback = withLoading(AZKPage);
const ReportsFallback = withLoading(ReportsPage);
const GenerateReportFallback = withLoading(GenerateReportPage);
const AccountFallback = withLoading(AccountPage);
const MoneyDepositFallback = withLoading(MoneyDepositPage);
const TransactionsOverviewFallback = withLoading(TransactionsOverviewPage);
const FaqPublicPage = React.lazy(() => import('@pages/FW/FaqPublicPage/FaqPublicPage'));
const FaqFallback = withLoading(FaqPage);
const FaqPublicFallback = withLoading(FaqPublicPage);
const WogBusinessPage = React.lazy(() => import('@pages/FW/WogBusiness/WogBusiness'));
const WogBusinessPublicPage = React.lazy(
	() => import('@pages/FW/WogBusinessPublicPage/WogBusinessPublicPage'),
);
const WogBusinessFallback = withLoading(WogBusinessPage);
const WogBusinessPublicFallback = withLoading(WogBusinessPublicPage);
const NotificationsFallback = withLoading(NotificationsPage);
const EDOFallback = withLoading(EDOPage);
const FuelPricesFallback = withLoading(FuelPricesPage);
const RegistrationFallback = withLoading(RegistrationPage);
const ApiDocsFallback = withLoading(ApiDocsPage);

const AppRouter: React.FC = () => {
	const protectedLayout = (
		<RequireAuth
			publicPaths={{
				'/faq': <FaqPublicFallback />,
				'/wog-business': <WogBusinessPublicFallback />,
			}}>
			<MainLayoutFallback />
		</RequireAuth>
	);

	return (
		<BrowserRouter>
			<RouterScrollToTop />
			<Routes>
				{/* Login. */}
				<Route path='auth' element={<AuthLayoutFallback />}>
					<Route path='' element={<Navigate to='/auth/login' replace />} />
					<Route path='login' element={<LoginForm />} />
					<Route path='selectClient' element={<SelectClientForm />} />
					<Route
						path='login/etalons'
						element={
							<Flex vertical gap={16} align='center'>
								<WIP />
								<Button>
									<Link to='/auth/login'>{t('userManagementEdit.return')}</Link>
								</Button>
							</Flex>
						}
					/>
					<Route path='verification' element={<VerificationForm />}></Route>
					<Route path='passwordRecovery' element={<PasswordRecoveryForm />} />
				</Route>

				<Route path='recover' element={<AuthLayoutFallback />}>
					<Route path='' element={<Navigate to='/auth/login' replace />} />
					<Route path=':userCode/:userID/' element={<ChangePassword />} />
				</Route>

				<Route path='registration' element={<AuthLayoutFallback />}>
					<Route path='' element={<RegistrationFallback />} />
				</Route>

				<Route path='/logout' element={<LogoutFallback />} />
				<Route path='/maintenance' element={<MaintenanceMode />} />
				<Route path='/api-docs/*' element={<ApiDocsFallback />} />

				{/* Fuel Wallets. */}
				<Route path='/' element={protectedLayout}>
					{/* New routing. */}
					<Route index element={<Home />} />

					<Route path='wallets'>
						<Route index element={<WalletsFallback />} />
						<Route path='transit' element={<TransitWalletFallback />} />
						<Route path='blocked-resources' element={<BlockedResourcesWalletFallback />} />
						<Route path=':walletId' element={<WalletFallback />} />
					</Route>

					<Route path='cards'>
						<Route index element={<CardsFallback />} />
						<Route path=':cardId' element={<CardFallback />} />
					</Route>

					<Route path='azk'>
						<Route path='' element={<AZKOverviewFallback />} />
						<Route path=':AZKid' element={<AZKFallback />} />
					</Route>

					<Route path='reports'>
						<Route path='' element={<ReportsFallback />} />
						<Route path=':reportId' element={<GenerateReportFallback />} />
						<Route path='archive' element={<ReportsFallback />} />
					</Route>

					<Route path='edo' element={<EDOFallback />} />

					<Route path='account' element={<AccountFallback />} />
					<Route path='money-deposit' element={<MoneyDepositFallback />} />
					<Route path='transactions' element={<TransactionsOverviewFallback />} />
					<Route path='faq' element={<FaqFallback />} />
					<Route path='wog-business' element={<WogBusinessFallback />} />
					<Route path='notifications' element={<NotificationsFallback />} />
					<Route path='fuel-prices' element={<FuelPricesFallback />} />

					{/* DEVELOPMENT IN PROGRESS. */}
					{/*<Route path='reserveSystem' element={<ReserveSystemFallback />} />*/}

					{/* ERRORS. */}
					<Route path='internal-server-error' element={<Error500 />} />
					<Route path='404' element={<Error404Page />} />
					<Route path='*' element={<Navigate to='/404' replace />} />
				</Route>

				{/* <Route path='profile' element={<Profile />} />*/}
				{/* <Route path='about-company' element={<AboutCompany />} />*/}

				{/*	<Route path='transit-account'>*/}
				{/*		<Route path='transit-account-settings' element={<TransitSettingsPage />} />*/}
				{/*		<Route path='cards' element={<TransitCardsPage />} />*/}
				{/*		<Route path='virtual-cards' element={<TransitVirtualCardsPage />} />*/}
				{/*	</Route>*/}

				{/*</Route>*/}
			</Routes>
		</BrowserRouter>
	);
};

export default AppRouter;
